<template>
  <div>
    <SearchToolbar
      class="mb-6"
      :placeholder="$t('OrganisationSearch')"
      :initial-keywords="keywords"
      :available-filters="availableFilters"
      :selected-filters="selectedFilters"
      @update-keyword-search="updateKeywordSearch"
      @update-filter-search="updateFilterSearch"
    />
    <div>
      <Table :columns="columns" :data="index" :is-row-clickable="true" @rowClick="redirectToOrganizationDetailsPage">
        <template #invoiceLevel="{ row }">
          <div
            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="{
              'bg-blue-100 text-blue-800': row.invoiceLevel === 'organization',
              'bg-green-100 text-green-800': row.invoiceLevel === 'store',
            }"
          >
            {{ $t(`invoice_level.${row.invoiceLevel}`) }}
          </div>
        </template>
        <template #source="{ row }">
          <div
            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="{
              'bg-blue-100 text-blue-800': row.source === 'web',
              'bg-green-100 text-green-800': row.source === 'retail',
              'bg-purple-100 text-purple-800': row.source === 'multichannel',
            }"
          >
            {{ $t(row.source) }}
          </div>
        </template>
      </Table>
    </div>
    <Pagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      :totalCount="totalCount"
      @changePage="handleChangePage"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { Pagination, SearchToolbar, Table, toCamelCase, isPresent } from "@estaly/ui";

export default {
  components: { Pagination, SearchToolbar, Table },

  async beforeMount() {
    this.loadQueryParams();
    await this.withLoader(this.retrieveOrganizations);
  },

  mounted() {
    this.availableFilters.sources = this.sources;
  },

  data() {
    return {
      currentPage: 1,
      keywords: null,
      totalCount: null,
      totalPages: null,
      availableFilters: {
        sources: [],
      },
      selectedFilters: {
        sources: [],
      },
      columns: [
        {
          label: "Id",
          field: "id",
          widthClass: "w-16",
        },
        {
          label: "Nom",
          field: "name",
          bold: true,
        },
        {
          label: "Raison sociale",
          field: "businessName",
        },
        {
          label: "Mode de facturation",
          field: "invoiceLevel",
          slot: "invoiceLevel",
        },
        {
          label: "Canal de vente",
          field: "source",
          slot: "source",
        },
      ],
    };
  },

  computed: {
    ...mapState("backoffice", ["isLoading"]),
    ...mapState("organizations", ["index"]),

    sources: {
      get() {
        return this.selectedFilters.sources;
      },
      set(value) {
        if (!isPresent(value)) return (this.selectedFilters.sources = []);

        this.selectedFilters.sources = Array.isArray(value) ? value : [value];
      },
    },

    searchParams() {
      return { ...this.$route.query, sources: this.sources };
    },
  },

  methods: {
    ...mapActions("backoffice", ["withLoader"]),
    ...mapActions("organizations", { fetchOrganizations: "fetchIndex" }),

    async retrieveOrganizations() {
      const response = await this.fetchOrganizations({ ...this.searchParams, page: this.currentPage });
      if (!response?.success) return;

      const data = toCamelCase(response.data);
      this.totalPages = data.page.totalPages;
      this.totalCount = data.page.totalCount;
    },

    updateRouteQueryParams() {
      const query = {};
      query.page = this.currentPage;
      if (this.keywords) query.keywords = this.keywords;
      if (this.sources) query.sources = this.sources;
      this.$router.push({ query });
    },

    loadQueryParams() {
      this.currentPage = parseInt(this.$route.query.page) || 1;
      this.keywords = this.$route.query.keywords;
      this.sources = this.$route.query.sources;
    },

    handleChangePage(page) {
      this.currentPage = page;
      this.updateRouteQueryParams();
    },

    updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.currentPage = 1;
      this.updateRouteQueryParams();
    },

    updateFilterSearch(filterName, filterOptions) {
      this.selectedFilters[filterName] = filterOptions;
      this.currentPage = 1;
      this.updateRouteQueryParams();
    },

    redirectToOrganizationDetailsPage(organization) {
      this.$router.push(`/organizations/${organization.id}`);
    },
  },

  watch: {
    "$route.query"() {
      this.withLoader(this.retrieveOrganizations);
    },
  },
};
</script>
